/**
 * Stores > Inventory Count at /stores/inventory.
 *
 * Mobile-first home for store inventory counting (replaces the AppSheet app).
 * Two actions: start a shelf count (cycle count against CounterPoint on-hand)
 * or receive an order (checks an arriving shipment against the PO it came
 * from). Open sessions can be resumed; recent submitted sessions are listed
 * for reference. Counting itself happens on /stores/inventory/:id.
 *
 * Gated by stores:store. An unmapped store user sees the "contact HQ" state;
 * an HQ user picks a store (same pattern as Today).
 *
 * The page content itself (hero buttons, session lists) is
 * `InventoryCount` from `@ampersandbrands/ui`, shared with The Sweet Spot.
 * This file is the fetch, the store-picking/access-denied chrome, and the
 * PO picker, none of which move because they differ per app (Cockpit lets an
 * HQ user act on any store; The Sweet Spot always acts on the signed-in
 * user's own store).
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { InventoryCount as SharedInventoryCount, PoPickerSheet } from '@ampersandbrands/ui';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { useSession } from '@/lib/useSession';
import {
  useStoreMe,
  useCountSessions,
  useCreateCountSession,
  useStorePos,
  hasStoresStore,
  hasStoresHq,
  isNotAvailable,
  isUnmapped,
  denverToday,
  type CountSession,
  type PoSummary,
} from '@/lib/useStores';
import { AccessDeniedCard, UnmappedCard, NotAvailableCard, ErrorCard } from './storesShared';
import {
  readInventoryStoreParam,
  hasInventoryStoreParam,
  readLastInventoryStore,
  writeLastInventoryStore,
} from './inventoryStoreParam';
import { InventoryStoreHeader } from './InventoryStoreHeader';

export function InventoryCount() {
  const { user } = useSession();
  const navigate = useNavigate();
  const canView = hasStoresStore(user?.permissions, user?.superAdmin);
  const isHq = hasStoresHq(user?.permissions, user?.superAdmin);

  // The picked store lives in the URL (?store=, with ?store_no= read as a
  // fallback for old links), not in component state, so coming BACK from a
  // count session (whose back link carries ?store=) restores the store
  // instead of making HQ pick it again, and a refresh keeps it. Same pattern
  // as InventoryVariance's URL-held filters.
  const [searchParams, setSearchParams] = useSearchParams();
  const hqStoreNo = readInventoryStoreParam(searchParams);
  const setHqStoreNo = (n: number | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (n === null) {
          next.delete('store');
          next.delete('store_no');
        } else {
          next.set('store', String(n));
          next.delete('store_no');
        }
        return next;
      },
      { replace: true },
    );
    if (n !== null) writeLastInventoryStore(n);
  };
  const scopedStoreNo = isHq && hqStoreNo !== null ? hqStoreNo : undefined;

  // Opening the page fresh (no store in the URL yet) reopens the last store
  // an HQ user picked anywhere in the inventory family (issue #507). Store
  // users are unaffected: their store comes from their own account mapping,
  // not from this param.
  useEffect(() => {
    if (!isHq || hqStoreNo !== null || hasInventoryStoreParam(searchParams)) return;
    const last = readLastInventoryStore();
    if (last !== null) setHqStoreNo(last);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHq]);

  const meQ = useStoreMe(scopedStoreNo);
  const sessionsQ = useCountSessions(undefined, scopedStoreNo);
  const createM = useCreateCountSession();
  const [pickingPo, setPickingPo] = useState(false);
  const posQ = useStorePos(scopedStoreNo, pickingPo);

  if (user && !canView) return <AccessDeniedCard what="Inventory Count" />;

  if (isUnmapped(meQ.error) || isUnmapped(sessionsQ.error)) {
    return (
      <div className="mx-auto w-full max-w-lg">
        <InventoryStoreHeader
          title="Inventory Count"
          actions={<ToolPinToggle toolSlug="stores-inventory" toolName="Inventory Count" />}
          showStorePicker={isHq}
          date={denverToday()}
          storeNo={hqStoreNo}
          onStoreChange={setHqStoreNo}
        />
        {isHq ? (
          <div className="rounded-lg border border-amp-line bg-amp-white p-6 dark:border-amp-dark-line dark:bg-amp-dark-surface">
            <h2 className="mb-1 text-lg font-semibold text-amp-ink dark:text-amp-dark-ink">
              Pick a store to view
            </h2>
            <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
              Your HQ account isn't tied to a single store. Choose a store above to run or review
              its counts.
            </p>
          </div>
        ) : (
          <UnmappedCard />
        )}
      </div>
    );
  }

  if (isNotAvailable(meQ.error) || isNotAvailable(sessionsQ.error)) {
    return (
      <div className="mx-auto w-full max-w-lg">
        <InventoryStoreHeader
          title="Inventory Count"
          actions={<ToolPinToggle toolSlug="stores-inventory" toolName="Inventory Count" />}
          showStorePicker={isHq}
          date={denverToday()}
          storeNo={hqStoreNo}
          onStoreChange={setHqStoreNo}
        />
        <NotAvailableCard what="Inventory Count" />
      </div>
    );
  }

  const storeName = meQ.data?.store?.store_name;
  const sessions = sessionsQ.data?.sessions ?? [];

  // The session page must act on the same store this page is scoped to. An HQ
  // user's own mapping (if any) differs from the picked store, so the store
  // rides the URL; without it the detail fetch resolves to the wrong store.
  const sessionQs = scopedStoreNo !== undefined ? `?store=${scopedStoreNo}` : '';

  const startShelf = () => {
    createM.mutate(
      { kind: 'shelf', ...(scopedStoreNo !== undefined ? { store_no: scopedStoreNo } : {}) },
      { onSuccess: (d) => navigate(`/stores/inventory/${d.session.id}${sessionQs}`) },
    );
  };

  const startReceiving = (po: PoSummary) => {
    createM.mutate(
      {
        kind: 'receiving',
        po_no: po.po_no,
        ...(scopedStoreNo !== undefined ? { store_no: scopedStoreNo } : {}),
      },
      { onSuccess: (d) => navigate(`/stores/inventory/${d.session.id}${sessionQs}`) },
    );
  };

  return (
    <div className="mx-auto w-full max-w-lg pb-24">
      <InventoryStoreHeader
        title="Inventory Count"
        subtitle={storeName}
        actions={<ToolPinToggle toolSlug="stores-inventory" toolName="Inventory Count" />}
        showStorePicker={isHq}
        date={denverToday()}
        storeNo={hqStoreNo}
        onStoreChange={setHqStoreNo}
      />

      <SharedInventoryCount
        sessions={sessions as CountSession[]}
        loading={sessionsQ.isLoading}
        starting={createM.isPending}
        startError={createM.error && <div className="mt-3"><ErrorCard message={createM.error.message} /></div>}
        onStartShelf={startShelf}
        onStartReceiving={() => setPickingPo(true)}
        onOpenSession={(s) => navigate(`/stores/inventory/${s.id}${sessionQs}`)}
      />

      <PoPickerSheet
        open={pickingPo}
        pos={posQ.data?.pos ?? []}
        loading={posQ.isLoading}
        error={posQ.error && <ErrorCard message={posQ.error.message} />}
        onPick={(po) => {
          setPickingPo(false);
          startReceiving(po as PoSummary);
        }}
        onClose={() => setPickingPo(false)}
      />
    </div>
  );
}
